// Mounting targets and the Views applets open in them (docs/applets.md). A View is an applet's interface in a
// container of its own (`view.el`, a plain element the applet or React renders into) that a target shows somewhere:
// the page's targets are floating windows (`window`: dragged by their title bar, resized at a corner, minimized,
// maximized (its button, the ⋯ menu, or a double click or tap on its title bar), put in the browser's full screen,
// closed, kept in front when clicked, remembered per browser in localStorage `webtop.windows`; full screen on a
// narrow screen), the two sidebars (`sidebar:left`, `sidebar:right`: docked, stacked sections; while one is open
// the webtop's tiles keep clear of it, through the safe area the layout already honours, client/lib/geometry.js) and
// `overlay`, a full-screen overlay like the page's own (client/lib/overlay.js: one open at a time, closed by Escape or a click
// outside its `keep` selector, which only hides the View; show() or focus() opens it again) and `desktop`, the place
// of the webtop itself: a View shown there takes the whole page under the notch in place of the open webtop (which is
// hidden, not unloaded, while it is shown; one desktop View is shown at a time, the last shown, and hiding it brings
// the webtop back). It is where whatever draws webtops is meant to live (the Webtops applet, applets/webtops). An applet
// with the `targets` permission registers more (registerTarget). A View moves between targets with its element,
// so what it shows (its state, React's included) comes along: the target only builds the frame around it. Views and
// targets are separate on purpose: a View says what it is, a target where it goes.
//   mountView(spec, content) -> handle: {el, target, title, show(), hide(), update(opts), setTarget(id), focus(),
//                                        dispose(), disposed, onDispose(fn)}
//   registerTarget(def, owner) -> dispose; a target is {id, name, capabilities, open(view) -> frame, close(view),
//                                 update?(view), show?(view), hide?(view)}; its views move to a window when it goes.
import { lineIcon, SCALE } from '../widgets/kit.js';
import { showMenu } from './menu.js';
import { closeOverlay, isOpen, openOverlay, overlay, restyleOverlay } from './overlay.js';
import { relayout } from './render.js';

const $windows = document.getElementById('windows');
const $sides = { 'sidebar:left': document.getElementById('sidebar-left'), 'sidebar:right': document.getElementById('sidebar-right') };
const WINDOWS_KEY = 'webtop.windows';
const WINDOW_DEFAULT = { w: 420, h: 320 }; // unscaled px
const WINDOW_MIN = { w: 200, h: 120 };
// How much of a window stays on screen when it is moved out of the way, off the left or right edge or the bottom
// (unscaled px), so its title bar can always be grabbed again; the top is never crossed.
const WINDOW_KEEP = 48;
const NARROW = 640; // CSS px: below this a window is the whole screen (style.css agrees)

const targets = new Map(); // id -> target
const views = new Map(); // key -> view
let zTop = 10;
let cascade = 0;

export const getTargets = () => [...targets.values()].map((t) => ({ id: t.id, name: t.name, capabilities: [...t.capabilities], owner: t.owner ?? null }));
export const hasTarget = (id) => targets.has(id);
const named = (id) => targets.get(id) ?? null;

export function registerTarget(def, owner = null) {
  if (!def?.id || typeof def.open !== 'function' || typeof def.close !== 'function') throw new Error('A target needs an id, open(view) and close(view)');
  if (targets.has(def.id)) throw new Error(`There is already a target "${def.id}"`);
  const t = { ...def, capabilities: Array.isArray(def.capabilities) ? def.capabilities : [], owner };
  targets.set(def.id, t);
  return () => {
    if (targets.get(def.id) !== t) return;
    targets.delete(def.id);
    for (const v of views.values()) if (v.target === def.id) move(v, 'window'); // never left without a place
  };
}

// ---- views
let serial = 0;
export function mountView({ applet = null, id = null, title = '', target = 'window', ...opts } = {}, content = null) {
  const view = {
    key: `${applet ?? 'page'}/${id ?? `view-${++serial}`}`, applet, id, title: String(title ?? ''), opts, target: null,
    el: document.createElement('div'), frame: null, hidden: false, disposed: false, onDispose: [],
  };
  view.el.className = 'view-content';
  if (content instanceof Node) view.el.append(content);
  else if (typeof content === 'string') view.el.textContent = content;
  views.set(view.key, view);
  move(view, targets.has(target) ? target : 'window');
  const handle = {
    get el() { return view.el; },
    get target() { return view.target; },
    get title() { return view.title; },
    get disposed() { return view.disposed; },
    show() { setHidden(view, false); return handle; },
    hide() { setHidden(view, true); return handle; },
    update(changes = {}) {
      if (typeof changes.title === 'string') view.title = changes.title;
      Object.assign(view.opts, changes);
      named(view.target)?.update?.(view);
      return handle;
    },
    setTarget(next) {
      if (!targets.has(next)) throw new Error(`No target "${next}"`);
      if (next !== view.target) move(view, next);
      return handle;
    },
    focus() { named(view.target)?.focus?.(view); return handle; },
    onDispose(fn) { view.onDispose.push(fn); return handle; },
    dispose() { disposeView(view); },
  };
  view.handle = handle;
  return handle;
}
function move(view, targetId) {
  if (view.target) named(view.target)?.close(view);
  view.target = targetId;
  view.frame = named(targetId).open(view);
  if (view.hidden) setHidden(view, true);
}
function setHidden(view, hidden) {
  view.hidden = hidden;
  const t = named(view.target);
  if (hidden ? t?.hide : t?.show) (hidden ? t.hide : t.show)(view);
  else if (view.frame) view.frame.hidden = hidden;
}
function disposeView(view) {
  if (view.disposed) return;
  view.disposed = true;
  named(view.target)?.close(view);
  views.delete(view.key);
  for (const fn of view.onDispose.splice(0)) { try { fn(); } catch (e) { console.error(e); } }
  view.el.remove();
}
export const listViews = (applet) => [...views.values()].filter((v) => !applet || v.applet === applet).map((v) => v.handle);
/** Menu items that move `view` to each other target (the window's and sidebar sections' ⋯ menu); not the overlay. */
function moveItems(view) {
  return [...targets.values()].filter((t) => t.id !== view.target && !t.capabilities.includes('overlay')).map((t) => ({ label: `Move to ${t.name}`, action: () => view.handle.setTarget(t.id) }));
}
const button = (icon, title, action) => {
  const b = document.createElement('button');
  b.type = 'button';
  b.title = title;
  b.setAttribute('aria-label', title);
  b.innerHTML = lineIcon(icon);
  b.addEventListener('click', (e) => { e.stopPropagation(); action(e); });
  return b;
};
// A window may be maximized unless its view says `maximizable: false` or `resizable: false`.
const maximizable = (view) => view.opts.maximizable !== false && view.opts.resizable !== false;
const closeView = (view) => { view.opts.onClose?.(); if (!view.disposed) disposeView(view); };

// ---- the window target
const rects = () => { try { return JSON.parse(localStorage.getItem(WINDOWS_KEY)) ?? {}; } catch { return {}; } };
function remember(view, r) {
  try {
    const all = rects();
    all[view.key] = r;
    const keys = Object.keys(all);
    for (const k of keys.slice(0, Math.max(0, keys.length - 50))) delete all[k]; // a bound: the oldest go
    localStorage.setItem(WINDOWS_KEY, JSON.stringify(all));
  } catch {}
}
const px = (v) => `${Math.round(v * SCALE)}px`;
function windowRect(view) {
  const kept = rects()[view.key];
  const w = Math.max(WINDOW_MIN.w, kept?.w ?? view.opts.width ?? WINDOW_DEFAULT.w);
  const h = Math.max(WINDOW_MIN.h, kept?.h ?? view.opts.height ?? WINDOW_DEFAULT.h);
  const sw = innerWidth / SCALE, sh = innerHeight / SCALE;
  let x = kept?.x ?? view.opts.x ?? 40 + (cascade % 8) * 28;
  let y = kept?.y ?? view.opts.y ?? 40 + (cascade % 8) * 28;
  if (kept == null && view.opts.x == null) cascade++;
  x = Math.max(WINDOW_KEEP - Math.min(w, sw), Math.min(x, sw - WINDOW_KEEP));
  y = Math.max(0, Math.min(y, sh - 60));
  return { x, y, w: Math.min(w, sw), h: Math.min(h, sh), max: kept?.max === true };
}
// Minimizing leaves a window's title bar, docked above the notch (centred, or across a narrow screen), the bars
// stacked upwards in the order they were minimized (each frame's --min-slot, style.css); a click on one brings its
// window back to where it was.
function setMinimized(frame, on) {
  frame.classList.toggle('minimized', on);
  if (on) frame.dataset.minimizedAt = String(Date.now()); else delete frame.dataset.minimizedAt;
  restack();
}
function restack() {
  const bars = [...$windows.querySelectorAll('.applet-window.minimized')].sort((a, b) => a.dataset.minimizedAt - b.dataset.minimizedAt);
  bars.forEach((f, i) => f.style.setProperty('--min-slot', String(i)));
}
// Maximized: the window fills the screen above the notch (style.css), its own place kept for Restore and remembered
// with it (`max`), so it opens maximized next time. A maximized window is not dragged or resized.
function setMaximized(frame, view, on) {
  if (on && isMinimized(frame)) setMinimized(frame, false);
  frame.classList.toggle('maximized', on);
  const b = frame.querySelector('.win-head .max-btn');
  if (b) {
    const label = on ? 'Restore' : 'Maximize';
    b.title = label;
    b.setAttribute('aria-label', label);
    b.innerHTML = lineIcon(on ? 'unmaximize' : 'maximize');
  }
  remember(view, { ...frame.rect, max: on || undefined });
}
const isMaximized = (frame) => frame.classList.contains('maximized');
const isMinimized = (frame) => frame.classList.contains('minimized');
// The maximize button (and ⋯ › Maximize, and the double tap): maximized or back to its own size; on a docked bar it
// always opens the window maximized, whatever it was before it was minimized.
const toggleMaximized = (frame, view) => setMaximized(frame, view, isMinimized(frame) || !isMaximized(frame));
// The browser's full screen for the window alone (Escape leaves it); the maximize button leaves it too.
const fullScreenOk = () => !!document.fullscreenEnabled && typeof Element.prototype.requestFullscreen === 'function';
function toggleFullScreen(frame) {
  if (document.fullscreenElement === frame) document.exitFullscreen?.();
  else frame.requestFullscreen?.().catch(() => {});
}
function applyRect(frame, r) {
  frame.style.left = px(r.x); frame.style.top = px(r.y); frame.style.width = px(r.w); frame.style.height = px(r.h);
}
const windowTarget = {
  id: 'window', name: 'Window', capabilities: ['move', 'resize', 'minimize', 'close', 'show', 'hide'],
  open(view) {
    const frame = document.createElement('section');
    frame.className = 'applet-window';
    frame.setAttribute('role', 'dialog');
    frame.dataset.view = view.key;
    const head = document.createElement('div');
    head.className = 'win-head';
    const title = document.createElement('span');
    title.className = 'win-title';
    head.append(title);
    const more = button('dots', 'More', (e) => showMenu(e.clientX, e.clientY, [
      { label: view.title || 'Window', cls: 'head' },
      ...moveItems(view),
      { label: frame.classList.contains('minimized') ? 'Restore' : 'Minimize', action: () => setMinimized(frame, !frame.classList.contains('minimized')) },
      ...(maximizable(view) && innerWidth >= NARROW ? [{ label: isMaximized(frame) && !isMinimized(frame) ? 'Restore Size' : 'Maximize', action: () => toggleMaximized(frame, view) }] : []),
      ...(fullScreenOk() ? [{ label: 'Full Screen', action: () => toggleFullScreen(frame) }] : []),
    ]));
    const min = button('minimize', 'Minimize', () => setMinimized(frame, !frame.classList.contains('minimized')));
    const max = button('maximize', 'Maximize', () => {
      if (document.fullscreenElement === frame) return toggleFullScreen(frame);
      toggleMaximized(frame, view);
    });
    max.classList.add('max-btn');
    // A click on a docked (minimized) bar restores it; a double click or tap on the title bar of an open window
    // maximizes or restores it (counted by hand from pointer events, which the drag below already takes, so touch
    // gets it too).
    let lastTap = null;
    let down = null;
    head.addEventListener('pointerdown', (e) => { down = { x: e.clientX, y: e.clientY }; });
    head.addEventListener('pointerup', (e) => {
      if (e.target.closest('button') || !down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 6) { lastTap = null; return; }
      if (frame.classList.contains('minimized')) { setMinimized(frame, false); lastTap = null; return; }
      const now = { t: e.timeStamp, x: e.clientX, y: e.clientY };
      if (lastTap && now.t - lastTap.t < 350 && Math.hypot(now.x - lastTap.x, now.y - lastTap.y) < 24) {
        lastTap = null;
        if (maximizable(view) && innerWidth >= NARROW) toggleMaximized(frame, view);
        return;
      }
      lastTap = now;
    });
    const close = button('close', 'Close', () => closeView(view));
    head.append(more, min, max, close);
    const body = document.createElement('div');
    body.className = 'win-body';
    body.append(view.el);
    const grip = document.createElement('div');
    grip.className = 'win-resize';
    grip.setAttribute('aria-hidden', 'true');
    frame.append(head, body, grip);
    const r = windowRect(view);
    applyRect(frame, r);
    frame.rect = { x: r.x, y: r.y, w: r.w, h: r.h };
    anchorWindow(frame);
    if (r.max && maximizable(view)) setMaximized(frame, view, true);
    frame.style.zIndex = ++zTop;
    frame.addEventListener('pointerdown', () => { if (frame.style.zIndex !== String(zTop)) frame.style.zIndex = ++zTop; }, true);
    dragWindow(frame, head, view, false);
    dragWindow(frame, grip, view, true);
    $windows.append(frame);
    view.frame = frame; // update() reads it
    this.update(view);
    return frame;
  },
  close(view) { view.el.remove(); view.frame?.remove(); view.frame = null; restack(); },
  update(view) {
    const f = view.frame;
    if (!f) return;
    f.querySelector('.win-title').textContent = view.title;
    f.setAttribute('aria-label', view.title || 'Window');
    f.querySelector('.win-head button[title="Close"]').hidden = view.opts.closable === false;
    f.querySelector('.win-head button[title="Minimize"]').hidden = view.opts.minimizable === false;
    f.querySelector('.win-head .max-btn').hidden = !maximizable(view);
    if (!maximizable(view) && isMaximized(f)) setMaximized(f, view, false);
    f.classList.toggle('fixed', view.opts.resizable === false);
    if (view.opts.width || view.opts.height) {
      const r = { ...f.rect, ...(view.opts.width && { w: view.opts.width }), ...(view.opts.height && { h: view.opts.height }) };
      f.rect = r;
      applyRect(f, r);
    }
  },
  focus(view) { if (view.frame) view.frame.style.zIndex = ++zTop; },
};
// Drag the frame by `grip`: moves it, or (`resize`) sizes it; the result is remembered for the next time.
function dragWindow(frame, grip, view, resize) {
  grip.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.target.closest('button') || innerWidth < NARROW) return;
    if (frame.classList.contains('minimized') || isMaximized(frame)) return; // a docked bar is clicked, a maximized window stays put
    if (resize && view.opts.resizable === false) return;
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY, r: { ...frame.rect } };
    grip.setPointerCapture(e.pointerId);
    frame.classList.add('moving');
    const onMove = (ev) => {
      const dx = (ev.clientX - start.x) / SCALE, dy = (ev.clientY - start.y) / SCALE;
      const r = resize
        ? { ...start.r, w: Math.max(WINDOW_MIN.w, start.r.w + dx), h: Math.max(WINDOW_MIN.h, start.r.h + dy) }
        : { ...start.r, x: Math.max(WINDOW_KEEP - start.r.w, Math.min(start.r.x + dx, innerWidth / SCALE - WINDOW_KEEP)), y: Math.max(0, Math.min(start.r.y + dy, innerHeight / SCALE - 32)) };
      frame.rect = r;
      applyRect(frame, r);
    };
    const onUp = () => {
      grip.removeEventListener('pointermove', onMove);
      grip.removeEventListener('pointerup', onUp);
      grip.removeEventListener('pointercancel', onUp);
      frame.classList.remove('moving');
      anchorWindow(frame);
      remember(view, frame.rect);
    };
    grip.addEventListener('pointermove', onMove);
    grip.addEventListener('pointerup', onUp);
    grip.addEventListener('pointercancel', onUp);
  });
}

// ---- the sidebar targets: stacked sections in an aside; the webtop keeps clear of an open one
function sidebarTarget(id, name) {
  const aside = $sides[id];
  const side = id.split(':')[1];
  const open = (on) => {
    if (aside.hidden === !on) return;
    aside.hidden = !on;
    document.body.classList.toggle(`sidebar-${side}-open`, on);
    relayout();
  };
  return {
    id, name, capabilities: ['dock', 'close', 'show', 'hide'],
    open(view) {
      const section = document.createElement('section');
      section.className = 'side-section';
      section.dataset.view = view.key;
      const head = document.createElement('div');
      head.className = 'side-head';
      const title = document.createElement('span');
      title.className = 'side-title';
      head.append(title, button('dots', 'More', (e) => showMenu(e.clientX, e.clientY, [{ label: view.title || name, cls: 'head' }, ...moveItems(view)])),
        button('close', 'Close', () => closeView(view)));
      const body = document.createElement('div');
      body.className = 'side-body';
      body.append(view.el);
      section.append(head, body);
      aside.append(section);
      view.frame = section;
      open(true);
      this.update(view);
      return section;
    },
    close(view) {
      view.el.remove();
      view.frame?.remove();
      view.frame = null;
      if (!aside.querySelector('.side-section')) open(false);
    },
    update(view) {
      const f = view.frame;
      if (!f) return;
      f.querySelector('.side-title').textContent = view.title;
      f.querySelector('.side-head button[title="Close"]').hidden = view.opts.closable === false;
    },
    focus(view) { view.frame?.scrollIntoView({ block: 'nearest' }); },
  };
}

// ---- the desktop target: one View at a time in the webtop's place (body.desktop-view hides #webtop)
const desktopShown = []; // the desktop Views not hidden, the one on top last
function showDesktop() {
  const top = desktopShown.at(-1) ?? null;
  for (const v of views.values()) if (v.target === 'desktop' && v.frame) v.frame.hidden = v !== top;
  document.body.classList.toggle('desktop-view', !!top);
  relayout();
}
const desktopTarget = {
  id: 'desktop', name: 'Desktop', capabilities: ['desktop', 'show', 'hide'],
  open(view) {
    const frame = document.createElement('div');
    frame.className = 'desktop-frame';
    frame.dataset.view = view.key;
    frame.append(view.el);
    $windows.before(frame);
    view.frame = frame;
    desktopTarget.update(view);
    desktopTarget.show(view);
    return frame;
  },
  close(view) {
    desktopTarget.hide(view);
    view.el.remove();
    view.frame?.remove();
    view.frame = null;
  },
  update(view) {
    const f = view.frame;
    if (!f) return;
    f.setAttribute('aria-label', view.title || 'Desktop');
    if (view.opts.domId) f.id = String(view.opts.domId); else f.removeAttribute('id');
  },
  show(view) {
    const i = desktopShown.indexOf(view);
    if (i >= 0) desktopShown.splice(i, 1);
    desktopShown.push(view);
    view.hidden = false;
    showDesktop();
  },
  hide(view) {
    const i = desktopShown.indexOf(view);
    if (i >= 0) desktopShown.splice(i, 1);
    view.hidden = true;
    showDesktop();
  },
  focus(view) { desktopTarget.show(view); },
};

// ---- the overlay target: a full-screen overlay (client/lib/overlay.js), the launcher's place. The View's options say how it
// behaves: `domId` (the element's id, what the assistant is told is open: openView()), `keep` (a selector for its content;
// a click anywhere else closes it; 'button, input' by default), `bodyClass` (set on <body> while it is open;
// `launcher-open` trims the notch), `button` (a selector of the page's button that shows it pressed while it is open),
// `onShow()` (may return the element to focus) and `onHide()`. Closing it (Escape, a click outside, another overlay
// opening) hides the View rather than disposing it.
const overlayTarget = {
  id: 'overlay', name: 'Full-Screen Overlay', capabilities: ['overlay', 'show', 'hide'],
  open(view) {
    const frame = document.createElement('div');
    frame.className = 'launcher view-overlay';
    frame.setAttribute('role', 'dialog');
    frame.dataset.view = view.key;
    frame.hidden = true;
    frame.append(view.el);
    $windows.before(frame);
    view.frame = frame;
    view.overlay = overlay({
      el: frame, keep: view.opts.keep ?? 'button, input',
      onOpen: () => view.opts.onShow?.() ?? null,
      onClose: () => { view.hidden = true; try { view.opts.onHide?.(); } catch (e) { console.error(e); } },
    });
    this.update(view);
    openOverlay(view.overlay);
    view.hidden = false;
    return frame;
  },
  close(view) {
    if (view.overlay && isOpen(view.overlay)) closeOverlay(view.overlay);
    view.el.remove();
    view.frame?.remove();
    view.frame = null;
    view.overlay = null;
  },
  update(view) {
    const f = view.frame;
    if (!f || !view.overlay) return;
    if (view.opts.domId) f.id = String(view.opts.domId); else f.removeAttribute('id');
    f.setAttribute('aria-label', view.title || 'Overlay');
    view.overlay.keep = view.opts.keep ?? 'button, input';
    restyleOverlay(view.overlay, { bodyClass: view.opts.bodyClass ?? null, button: view.opts.button ? document.querySelector(view.opts.button) : null });
  },
  show(view) { if (view.overlay) openOverlay(view.overlay); },
  hide(view) { if (view.overlay) closeOverlay(view.overlay); },
  focus(view) { this.show(view); view.hidden = false; },
};

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
// A window moved out of the way past the right edge or the bottom stays there while the browser window changes size:
// it keeps its distance from that edge (one past the left keeps its own x, so it already does), so the same part of it
// stays in view. Which edges a window follows is settled where it is put (dropped or opened, `anchorWindow`), not by
// where a smaller screen happens to push it, so a window the screen only crowded comes back when the screen grows.
// Every window is kept within reach (WINDOW_KEEP), and the new places are remembered once the resizing settles.
function anchorWindow(frame) {
  const r = frame.rect;
  frame.anchor = { right: r.x + r.w > innerWidth / SCALE, bottom: r.y + r.h > innerHeight / SCALE };
}
let screenSize = null;
let resizeSave = 0;
const resized = new Set(); // views moved since the last save
function followEdges() {
  const sw = innerWidth / SCALE, sh = innerHeight / SCALE;
  const was = screenSize ?? { w: sw, h: sh };
  screenSize = { w: sw, h: sh };
  const dw = sw - was.w, dh = sh - was.h;
  if (!dw && !dh) return;
  for (const view of views.values()) {
    const f = view.frame;
    if (view.target !== 'window' || !f?.rect) continue;
    const r = { ...f.rect };
    if (f.anchor?.right) r.x += dw;
    if (f.anchor?.bottom) r.y += dh;
    r.x = Math.max(WINDOW_KEEP - r.w, Math.min(r.x, sw - WINDOW_KEEP));
    r.y = Math.max(0, Math.min(r.y, sh - 32));
    if (r.x === f.rect.x && r.y === f.rect.y) continue;
    f.rect = r;
    applyRect(f, r);
    resized.add(view);
  }
  if (!resized.size) return;
  clearTimeout(resizeSave);
  resizeSave = setTimeout(() => {
    for (const view of resized) if (view.frame?.rect) remember(view, { ...view.frame.rect, max: isMaximized(view.frame) || undefined });
    resized.clear();
  }, 400);
}

export function init() {
  screenSize = { w: innerWidth / SCALE, h: innerHeight / SCALE };
  addEventListener('resize', followEdges);
  registerTarget(windowTarget);
  registerTarget(sidebarTarget('sidebar:left', 'Left Sidebar'));
  registerTarget(sidebarTarget('sidebar:right', 'Right Sidebar'));
  registerTarget(overlayTarget);
  registerTarget(desktopTarget);
}
